/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { EtagDictionaryMapper, ID } from '@gh/common-shared-data';
import {
  AbstractRestService,
  arrayOf, emptyOf,
  identity, noAuthRefreshOf,
  optimisticLockingOf,
  RestSettings,
  SlicedDataSource,
} from '@gh/core-data';
import { currentDate } from '@gh/core-util';
import { fromPairs, get, map } from 'lodash';
import { Observable } from 'rxjs';

import {
  TodoChangeStatusResponseDto,
  TodoDetailsDto,
  TodoFormDto,
  TodoListDto,
  TodoStatus,
} from './todo.dto';
import {
  TodoChangeStatusRequestMapper,
  TodoChangeStatusResponseMapper,
  TodoDetailsMapper,
  TodoListMapper,
} from './todo.mapper';

export function idsFromList<T>(list: T[]): ID[] {
  return map(list, (item: any): ID => <ID>get(item, 'id'));
}

export function etagList(list: TodoListDto[]): Hash<string> {
  return fromPairs(map(list, (item: TodoListDto) => [String(item.id), String(item.etag)]));
}

export type ChangeStatusParams = {
  fromStatus: TodoStatus;
  toStatus: TodoStatus;
  todoList: TodoListDto[];
  userId?: Maybe<ID>;
};

export type ChangeStatusQuery = {
  fromStatus: TodoStatus;
  toStatus: TodoStatus;
  todoWorkflows: Hash<string>;
  userId?: Maybe<ID>;
};

@Injectable()
export class TodoRestService extends AbstractRestService {

  /**
   *
   * @returns {{id: ID, subject: string, message: string, assigneeName: string, assignorName: string, status: any, dueDate: Date}}
   */
  static prepareListForCreate(): TodoListDto {
    return {
      id: '',
      subject: '',
      message: '',
      assigneeName: '',
      assignorName: '',
      status: TodoStatus.INBOX,
      dueDate: currentDate(),
    };
  }

  /**
   *
   * @returns {{id: ID, subject: string, message: string, assignee: any, status: any, dueDate: Date}}
   */
  static prepareItemForCreate(): TodoFormDto {
    return {
      id: '',
      subject: '',
      message: '',
      assignee: void 0,
      status: TodoStatus.INBOX,
      dueDate: currentDate(),
    };
  }

  constructor(settings: RestSettings) {
    super(settings);
  }

  /**
   * @param fromStatus
   * @param toStatus
   * @param todoList
   * @param userId
   */
  changeStatus({ fromStatus, toStatus, todoList, userId }: ChangeStatusParams): Observable<TodoChangeStatusResponseDto> {
    return this.httpPut(
      `${this.bp}/todos/change-status`,
      {
        fromStatus,
        toStatus,
        todoWorkflows: etagList(todoList),
        userId,
      },
      optimisticLockingOf(TodoChangeStatusRequestMapper),
      optimisticLockingOf(TodoChangeStatusResponseMapper),
    );
  }

  checkNewToDo(): Observable<number> {
    return this.httpGet(`${this.bp}/todos/count-new`, {}, emptyOf(noAuthRefreshOf(identity())), identity());
  }

  /**
   *
   * @param todo
   * @returns {Observable<TodoDetailsDto>}
   */
  create(todo: TodoDetailsDto): Observable<TodoDetailsDto> {
    return this.httpPost(`${this.bp}/todos`, todo, optimisticLockingOf(TodoDetailsMapper));
  }

  /**
   *
   * @param todo
   * @returns {Observable<TodoDetailsDto>}
   */
  update(todo: TodoDetailsDto): Observable<TodoDetailsDto> {
    return this.httpPut(`${this.bp}/todos/${todo.id}`, todo, optimisticLockingOf(TodoDetailsMapper));
  }

  /**
   *
   * @param todoList
   * @returns {Observable<TodoListDto[]>}
   */
  remove(todoList: TodoListDto[]): Observable<TodoListDto[]> {
    return this.httpPut(`${this.bp}/todos/delete`, etagList(todoList), EtagDictionaryMapper, arrayOf(TodoListMapper));
  }

  /**
   *
   * @param id
   * @returns {Observable<TodoDetailsDto>}
   */
  get(id: ID): Observable<TodoDetailsDto> {
    return this.httpGet(`${this.bp}/todos/${id}`, optimisticLockingOf(TodoDetailsMapper));
  }

  /**
   *
   * @param filter
   * @returns {SlicedDataSource<TodoListDto>}
   */
  findAllAsDataSource(filter: any): SlicedDataSource<TodoListDto> {
    return this.createSlicedDataSourceGet(`${this.bp}/todos`, filter, void 0, arrayOf(TodoListMapper));
  }
}
